/**
 * Canonical status models.
 *
 * Status NAMES are configurable per laboratory; their MEANINGS are fixed.
 * These constants are the internal identifiers — display labels come from the
 * i18n catalogues, never from these values.
 *
 * Source: requirements document p.22.
 */
// ---------------------------------------------------------------------------
// Service request / work order
// ---------------------------------------------------------------------------
export const WORK_ORDER_STATUS = {
    NEW: 'new',
    /** Created before the sample physically arrived. Registering a request is
     *  never evidence that a sample arrived. */
    AWAITING_SAMPLE: 'awaiting_sample',
    IN_PROGRESS: 'in_progress',
    /** Some tests are finished, others are not. Never shown as complete. */
    PARTIALLY_COMPLETE: 'partially_complete',
    COMPLETE: 'complete',
    /** Cancelled with a recorded reason. Records are preserved, never deleted. */
    CANCELLED: 'cancelled',
};
// ---------------------------------------------------------------------------
// Sample acceptance
// ---------------------------------------------------------------------------
export const SAMPLE_STATUS = {
    AWAITING_INSPECTION: 'awaiting_inspection',
    ACCEPTED: 'accepted',
    /** Accepted, but with recorded reservations. Proceeds to testing. */
    ACCEPTED_WITH_NOTES: 'accepted_with_notes',
    /** Held pending missing data. Does NOT proceed to testing. */
    HELD: 'held',
    /** Rejected with a recorded reason. Does NOT proceed to testing. */
    REJECTED: 'rejected',
};
/**
 * Only accepted samples may be assigned tests.
 * Held and rejected samples retain their number, decision and reason, so the
 * receiving event always leaves a trace.
 */
export const SAMPLE_STATUSES_ALLOWING_TESTING = Object.freeze([SAMPLE_STATUS.ACCEPTED, SAMPLE_STATUS.ACCEPTED_WITH_NOTES]);
export function sampleMayBeAssignedTests(status) {
    return SAMPLE_STATUSES_ALLOWING_TESTING.includes(status);
}
// ---------------------------------------------------------------------------
// Test execution
// ---------------------------------------------------------------------------
export const TEST_STATUS = {
    NOT_STARTED: 'not_started',
    IN_PROGRESS: 'in_progress',
    /** Paused for a recorded reason. Not a cancellation — work resumes. */
    TEMPORARILY_STOPPED: 'temporarily_stopped',
    AWAITING_REVIEW: 'awaiting_review',
    RETURNED_FOR_CORRECTION: 'returned_for_correction',
    /**
     * A reviewer required the work to be PERFORMED AGAIN, with a reason. It is
     * not "returned for correction": correcting a record and repeating a test
     * are different acts, and a laboratory that cannot tell them apart cannot
     * account for either (Phase 9, D98).
     */
    RETEST_REQUIRED: 'retest_required',
    /** Technically accepted by review, now awaiting the separate approval stage. */
    AWAITING_APPROVAL: 'awaiting_approval',
    /** Approved and LOCKED. Corrections require a recorded procedure. */
    APPROVED: 'approved',
    CANCELLED: 'cancelled',
};
/** Approved results are locked against direct editing. */
export function testResultIsLocked(status) {
    return status === TEST_STATUS.APPROVED;
}
// ---------------------------------------------------------------------------
// Report version
// ---------------------------------------------------------------------------
export const REPORT_VERSION_STATUS = {
    DRAFT: 'draft',
    /** Registered with the laboratory, not replaced or cancelled. */
    ISSUED_VALID: 'issued_valid',
    /** An open review requires consulting the laboratory before relying on it. */
    SUSPENDED: 'suspended',
    /** A later version exists. Retained, never deleted. */
    SUPERSEDED: 'superseded',
    /** Cancelled by the laboratory. The number and date are preserved and the
     *  number is never reused. */
    CANCELLED: 'cancelled',
    /**
     * A DRAFT that was abandoned — a report that was never issued, or a
     * replacement draft closed because the report it would have replaced was
     * cancelled (D131). Terminal, and it never carries a report number: a
     * number belongs to a document, and a discarded draft never became one.
     *
     * It is NOT a deletion. The row, its preparer and the stated reason stay,
     * because somebody did prepare it and the laboratory may need to say why it
     * went no further. Nothing in Phase 10 is deleted.
     */
    DISCARDED: 'discarded',
};
export const ALL_REPORT_VERSION_STATUSES = Object.freeze(Object.values(REPORT_VERSION_STATUS));
/**
 * The status of a REPORT, as opposed to one of its versions.
 *
 * A report is a controlled document with a history of versions, and what a
 * register or a client is told about it is the state of the version that
 * counts NOW: the current one, or the draft that has not become a document
 * yet. So this list is deliberately the version list MINUS `superseded` —
 * a superseded version is a fact about a version, never about the report,
 * which by definition has a newer one.
 *
 * Derived, never stored: the report row holds no status column, because a
 * second copy of this answer is a second thing to keep in agreement with the
 * versions, and the day it disagreed a cancelled report would read as valid.
 */
export const REPORT_STATUS = {
    /** Prepared, not issued. It has no number and is not a document yet. */
    DRAFT: 'draft',
    ISSUED_VALID: 'issued_valid',
    SUSPENDED: 'suspended',
    /** The number, the dates and every file are kept, and the number is never
     *  reused. */
    CANCELLED: 'cancelled',
    /** Prepared and abandoned before ever being issued. */
    DISCARDED: 'discarded',
};
export const ALL_REPORT_STATUSES = Object.freeze(Object.values(REPORT_STATUS));
// ---------------------------------------------------------------------------
// Report delivery — DISTINCT from issuance
// ---------------------------------------------------------------------------
export const REPORT_DELIVERY_STATUS = {
    /** Issued and ready, but deliberately not yet released to the client. */
    NOT_RELEASED: 'not_released',
    /**
     * The headline version is currently RELEASED to the client portal by a
     * recorded act (Phase 11, D149), and no delivery has been recorded. Phase 10
     * never produced it, because it had no portal (D121). Availability is not
     * delivery and a portal download is not receipt.
     */
    AVAILABLE_TO_CLIENT: 'available_to_client',
    SENT: 'sent',
    /** Only set where actual proof of receipt exists. A staff download is not
     *  receipt, and portal availability is not proof of reading. */
    RECEIPT_CONFIRMED: 'receipt_confirmed',
};
export const ALL_REPORT_DELIVERY_STATUSES = Object.freeze(Object.values(REPORT_DELIVERY_STATUS));
// ---------------------------------------------------------------------------
// QR verification outcomes — what a link-holder sees
// ---------------------------------------------------------------------------
export const VERIFICATION_OUTCOME = {
    ISSUED_VALID: 'issued_valid',
    /** A later version exists. The endpoint serves the latest valid version. */
    SUPERSEDED: 'superseded',
    /** No PDF is returned. */
    CANCELLED: 'cancelled',
    /** No PDF is returned. */
    SUSPENDED: 'suspended',
    NOT_FOUND: 'not_found',
    /** Service temporarily unavailable. This does NOT imply the report is invalid. */
    UNAVAILABLE: 'unavailable',
};
/** Outcomes for which the endpoint returns a PDF. */
export const VERIFICATION_OUTCOMES_ALLOWING_DOWNLOAD = Object.freeze([
    VERIFICATION_OUTCOME.ISSUED_VALID,
    // A superseded token resolves forward to the latest valid version.
    VERIFICATION_OUTCOME.SUPERSEDED,
]);
export function verificationAllowsDownload(outcome) {
    return VERIFICATION_OUTCOMES_ALLOWING_DOWNLOAD.includes(outcome);
}
// ---------------------------------------------------------------------------
// Invoice payment — INDEPENDENT of test and report status
// ---------------------------------------------------------------------------
/**
 * The lifecycle an invoice is STORED in (Phase 11, D152):
 * `draft → issued → cancelled`, or `draft → discarded`, and nothing else.
 */
export const INVOICE_STATUS = {
    /** Being written. No number, no issue date, not yet a financial fact. */
    DRAFT: 'draft',
    /** Number, dates, rate, totals and both identity snapshots are frozen. */
    ISSUED: 'issued',
    /** Cancellation and settlement are handled as recorded acts, never by
     *  deleting the transaction. A cancelled invoice keeps its number. */
    CANCELLED: 'cancelled',
    /**
     * A DRAFT that was abandoned, with its reason. Terminal, and it never
     * carries a number: a number belongs to a document, and a discarded draft
     * never became one (D143). It is NOT a deletion — the row stays.
     */
    DISCARDED: 'discarded',
};
export const ALL_INVOICE_STATUSES = Object.freeze(Object.values(INVOICE_STATUS));
/**
 * How much of an ISSUED invoice has been settled. DERIVED, never stored
 * (D152): see `deriveInvoicePaymentStatus` in `finance.ts`. A draft, a
 * discarded draft and a cancelled invoice have no payment status at all.
 */
export const PAYMENT_STATUS = {
    UNPAID: 'unpaid',
    PARTIALLY_PAID: 'partially_paid',
    PAID: 'paid',
};
export const ALL_PAYMENT_STATUSES = Object.freeze(Object.values(PAYMENT_STATUS));
/**
 * The state of a PAYMENT RECORD (money received), as opposed to the payment
 * status of an invoice above. A payment recorded in error is REVERSED with a
 * reason — never deleted — and stays on the record and the statement (D153).
 */
export const PAYMENT_RECORD_STATUS = {
    RECORDED: 'recorded',
    REVERSED: 'reversed',
};
export const ALL_PAYMENT_RECORD_STATUSES = Object.freeze(Object.values(PAYMENT_RECORD_STATUS));
// ---------------------------------------------------------------------------
// Quotation
// ---------------------------------------------------------------------------
export const QUOTATION_STATUS = {
    DRAFT: 'draft',
    SENT: 'sent',
    /** Set only by explicitly recording the client's response.
     *  Printing a quotation is NEVER acceptance. */
    ACCEPTED: 'accepted',
    REJECTED: 'rejected',
    EXPIRED: 'expired',
};
/**
 * Every quotation state the interface can DISPLAY, including the derived one.
 *
 * `expired` is not stored anywhere (see `pricing.ts`): it is computed from the
 * validity date, so the date remains the single source of truth and no
 * background job has to keep a column agreeing with it. It appears here
 * because a person still has to read the word on a screen, and a filter still
 * has to offer it.
 */
export const ALL_QUOTATION_STATUSES = Object.freeze(Object.values(QUOTATION_STATUS));
// ---------------------------------------------------------------------------
// Test template readiness — the scientific-implementation gate
// ---------------------------------------------------------------------------
/**
 * Whether a test has a validated specialised template.
 *
 * A test that is NOT_READY uses only the general workflow and must never
 * display invented scientific fields, formulas or charts.
 * See CLAUDE.md section 4.
 */
export const TEMPLATE_READINESS = {
    /** No specialised template. General workflow only. This is the default and
     *  the state of all five initial tests. */
    NOT_READY: 'not_ready',
    /** Specification received from the laboratory; implementation in progress. */
    IN_PREPARATION: 'in_preparation',
    /** Implemented and awaiting laboratory specialist certification. */
    AWAITING_CERTIFICATION: 'awaiting_certification',
    /** Certified against a validated worked example. Safe for live use. */
    READY: 'ready',
};
export const ALL_TEMPLATE_READINESS = Object.freeze(Object.values(TEMPLATE_READINESS));
/**
 * May this test present specialised scientific input fields and calculations?
 * Only a certified template may. Everything else uses the general path.
 */
export function mayUseSpecialisedTemplate(readiness) {
    return readiness === TEMPLATE_READINESS.READY;
}
//# sourceMappingURL=status.js.map